import React from "react";
import { CheckCircle2, QrCode, ShieldCheck } from "lucide-react";

const STANDARDS = [
  {
    code: "NIST SP 800-88 Rev. 2",
    desc: "U.S. government guidelines for media sanitization (Clear & Purge). Our primary standard, named on every certificate.",
  },
  {
    code: "IEEE 2883-2022",
    desc: "The international standard for storage sanitization.",
  },
  {
    code: "DoD 5220.22-M",
    desc: "The classic 3-pass overwrite specification — our 3-pass option.",
  },
  {
    code: "DoD 5220.22-M (ECE)",
    desc: "The extended 7-pass overwrite specification — our 7-pass option.",
  },
];

const RECORDS = [
  "Device & drive identity (model, serial, firmware)",
  "Erasure method used per drive",
  "Verification result (pass / fail)",
  "The standard applied",
  "Timestamp & station details",
];

const Certificate = () => {
  return (
    <section id="certificate" className="relative bg-brand-bg font-body">
      <div className="mx-auto max-w-7xl px-6 py-20 lg:py-28">
        <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
          {/* left: copy + standards */}
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-brand-plum">
              Standards &amp; certificates
            </span>
            <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-brand-plum sm:text-4xl lg:text-5xl">
              Proof Your Customer Can <span className="text-brand-gradient">Verify Themselves</span>
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-ink sm:text-lg">
              Every erasure is performed and documented against recognised international standards. We follow
              their core principle — prefer the drive&apos;s built-in firmware sanitize over software
              overwrite, and verify the result rather than assuming it.
            </p>

            <div className="mt-8 space-y-3">
              {STANDARDS.map(({ code, desc }) => (
                <div key={code} className="flex gap-3 rounded-2xl border border-line bg-white p-4">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand-magenta" />
                  <div>
                    <div className="font-display text-sm font-semibold text-brand-plum">{code}</div>
                    <p className="mt-0.5 text-sm leading-relaxed text-muted">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* right: certificate detail card */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <div className="overflow-hidden rounded-3xl border border-line bg-white shadow-xl shadow-brand-plum/5">
              <div className="flex items-center justify-between border-b border-line bg-brand-bg px-6 py-4">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-brand-magenta" />
                  <span className="font-display text-base font-semibold text-brand-plum">
                    Tamper-evident certificate
                  </span>
                </div>
                <span className="inline-flex items-center gap-1 rounded-full bg-success/15 px-2.5 py-0.5 text-xs font-semibold text-success">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Signed
                </span>
              </div>

              <div className="px-6 py-5">
                <p className="text-sm font-medium text-brand-plum">Each certificate records:</p>
                <ul className="mt-3 list-none space-y-2 pl-0">
                  {RECORDS.map((r) => (
                    <li key={r} className="flex items-start gap-2 text-sm text-muted">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                      {r}
                    </li>
                  ))}
                </ul>

                <div className="mt-5 flex items-center gap-4 rounded-2xl border border-brand-orchid/30 bg-brand-wash/60 p-4">
                  <span className="inline-flex h-16 w-16 shrink-0 items-center justify-center rounded-xl border border-line bg-white text-brand-plum">
                    <QrCode className="h-10 w-10" strokeWidth={1.2} />
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-brand-plum">A trust signal, not a chore</div>
                    <p className="mt-1 text-xs leading-relaxed text-muted">
                      Each certificate carries a unique, scannable code. The buyer of the refurbished device
                      can scan it and independently confirm it was erased to standard — proof you can put
                      right on the resale listing.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Certificate;
